import * as logger from 'loglevel';
import { getUuid } from '@front10/helpers/dist/uuid';
import dayjs from 'dayjs';
import { getDateRangeAsDates } from '../../helpers/localization/localizationUtils';
import transformRequestFeatured from './transformRequest/transformFeaturedPopularDestinations';
import axiosWrapper from '../../helpers/axiosWrapper';
import { yearAdjustmentPatch } from './yearAdjustmentPatch';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';

/**
 * Makes sure the fares response is complain with a basic schema
 * @param {*} fares
 */
const validateFaresResponse = (fares) => {
  const validation = { isValid: true, message: '' };
  if (!fares || !Array.isArray(fares)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: fares,
      message: 'INTEGRATION__FARES: Fares must be an Array'
    };
  }
  if (fares.length > 0) {
    const fareItem = fares[0];
    if (!fareItem.origin || !fareItem.destination) {
      return {
        isValid: false,
        data: fareItem,
        message:
          'INTEGRATION__FARES: FareItem SCHEMA is invalid, please check Fares API documentation.'
      };
    }
  }
  return validation;
};

/**
 * The group-routes API does not returns the journeyType
 * so this map allows us to use query sent parameters to do it
 */
const journeyTypeMap = {
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformData = (faresRawData, query) => {
  const { uiDatesYearsAdjustmentPatch } = query;
  let fares = [];
  faresRawData.forEach((fare) => {
    let newFare = Object.assign({}, fare);
    delete newFare.fares;
    fare.fares.map((data) => {
      let { searchDate } = data;
      // For date in safary & ie
      if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
        searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
          searchDate.length - 2
        )}`;
      }
      newFare = Object.assign({}, newFare, { ...data, searchDate });
      return data;
    });
    if (newFare.redemption && newFare.formattedTotalPrice) {
      newFare.redemption.formattedAmount = newFare.formattedTotalPrice;
    }
    newFare.journeyType = journeyTypeMap[newFare.flightType];
    newFare.index = getUuid();
    newFare.destinationImage =
      newFare.destinationAirportImage ||
      newFare.destinationCityImage ||
      newFare.destinationStateImage ||
      newFare.destinationCountryImage ||
      newFare.destinationRegionImage;
    yearAdjustmentPatch(newFare, uiDatesYearsAdjustmentPatch);
    fares.push(newFare);
  });
  const sorting = query.sorting[0];
  if (sorting) {
    if (sorting.usdTotalPrice) {
      if (sorting.usdTotalPrice.toLowerCase() === 'asc') {
        fares = fares.sort((a, b) => (a.usdTotalPrice < b.usdTotalPrice ? -1 : 1));
      }
      if (sorting.usdTotalPrice.toLowerCase() === 'desc') {
        fares = fares.sort((a, b) => (a.usdTotalPrice > b.usdTotalPrice ? -1 : 1));
      }
    } else if (sorting.departureDate) {
      if (sorting.departureDate.toLowerCase() === 'asc') {
        fares = fares.sort((a, b) => {
          if (dayjs(a.departureDate).isBefore(dayjs(b.departureDate))) {
            return -1;
          }
          if (dayjs(a.departureDate).isAfter(dayjs(b.departureDate))) {
            return 1;
          }
          return 0;
        });
      }
    }
  }
  const mostPopularFare = fares.shift();
  return {
    popularDestinations: fares,
    topPopularDestination: mostPopularFare,
    originCityName:
      fares[0] && fares[0].originCity && fares[0].origin
        ? `${fares[0].originCity} (${fares[0].origin})`
        : ''
  };
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformAggregationData = (faresRawData, query) => {
  const { uiDatesYearsAdjustmentPatch } = query;
  const fares = [];
  faresRawData.forEach((fare) => {
    let newFare = Object.assign({}, fare);
    delete newFare.fares;

    let { searchDate } = fare;
    // For date in safary y ie
    if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
      searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
        searchDate.length - 2
      )}`;
    }
    newFare = Object.assign({}, newFare, { ...fare, searchDate });

    if (newFare.redemption && newFare.formattedTotalPrice) {
      newFare.redemption.formattedAmount = newFare.formattedTotalPrice;
    }
    newFare.journeyType = journeyTypeMap[newFare.flightType];
    newFare.index = getUuid();
    newFare.destinationImage =
      newFare.destinationAirportImage ||
      newFare.destinationCityImage ||
      newFare.destinationStateImage ||
      newFare.destinationCountryImage ||
      newFare.destinationRegionImage;
    yearAdjustmentPatch(newFare, uiDatesYearsAdjustmentPatch);
    fares.push(newFare);
  });

  const mostPopularFare = fares.shift();
  return {
    popularDestinations: fares,
    topPopularDestination: mostPopularFare,
    originCityName:
      fares[0] && fares[0].originCity && fares[0].origin
        ? `${fares[0].originCity} (${fares[0].origin})`
        : ''
  };
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getFaresWithAggregation = (filter, integrationSettings, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__FARES__AGREGGATIONS: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    const { v2FaresAggregations: faresApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = faresApiSettings;

    const filterCopy = { ...filter };

    if (
      !faresApiSettings ||
      !faresApiSettings.method ||
      !faresApiSettings.endPoint ||
      !faresApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__FARES__AGREGGATIONS: **integrationSettings.faresGroupRoutes** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    filterCopy.departure = getDateRangeAsDates(
      filterCopy.departure,
      'YYYY-MM-DD',
      'INTEGRATION__FARES__AGREGGATIONS'
    );
    if (filterCopy.return) {
      filterCopy.return = getDateRangeAsDates(
        filterCopy.return,
        'YYYY-MM-DD',
        'INTEGRATION__FARES__AGREGGATIONS'
      );
    }

    axiosWrapper(
      {
        method: faresApiSettings.method,
        url: `${faresApiSettings.endPoint}`,
        headers: { ...faresApiSettings.headers },
        data: transformRequestFeatured(filterCopy)
      },
      {
        resource: 'INTEGRATION__FARES__AGREGGATIONS',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validateFaresResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__FARES__AGREGGATIONS: Successful request', response.data);
          const fares = transformAggregationData(response.data, filter);
          resolve(fares);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getFares = (filter, integrationSettings, withAggregations, meta, pageContext) => {
  if (withAggregations === 'agreggation' || withAggregations === 'aggregation') {
    return getFaresWithAggregation(filter, integrationSettings, meta);
  }

  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    const { v2FaresGroupRoutes: faresApiSettings } = integrationSettings;
    const filterCopy = { ...filter };

    if (
      !faresApiSettings ||
      !faresApiSettings.method ||
      !faresApiSettings.endPoint ||
      !faresApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings.faresGroupRoutes** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    filterCopy.departure = getDateRangeAsDates(
      filterCopy.departure,
      'YYYY-MM-DD',
      'INTEGRATION__FARES'
    );
    if (filterCopy.return) {
      filterCopy.return = getDateRangeAsDates(
        filterCopy.return,
        'YYYY-MM-DD',
        'INTEGRATION__FARES'
      );
    }

    axiosWrapper(
      {
        method: faresApiSettings.method,
        url: `${faresApiSettings.endPoint}`,
        headers: { ...faresApiSettings.headers },
        data: transformRequestFeatured(filterCopy)
      },
      { resource: 'INTEGRATION__FARES__GROUP_ROUTES', meta, integrationSettings, pageContext }
    )
      .then((response) => {
        const validation = validateFaresResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__FARES: Successful request', response.data);
          const fares = transformData(response.data, filter);
          resolve(fares);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getFares, transformData, getDateRangeAsDates };
